import React, { useEffect, useMemo, useRef, useState } from "react";

import { IEspmPropertyTypeCategoryNode } from "../../../services/zeos-api/contracts/EspmPropertyType";
import {
  buildEspmFlatOptions,
  findEspmFlatOption,
  formatEspmSelectionLabel,
  IEspmFlatOption,
  IEspmTypeaheadValue,
} from "./espmFlatOptions";

const FILTERED_RESULT_LIMIT = 8;

interface IProps {
  categories: IEspmPropertyTypeCategoryNode[];
  value?: IEspmTypeaheadValue;
  onChange: (next: IEspmTypeaheadValue | undefined, label: string) => void;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  hint?: string;
}

export const EspmPropertyTypeTypeahead: React.FC<IProps> = ({
  categories,
  value,
  onChange,
  disabled = false,
  loading = false,
  label = "Property type",
  hint = "Typeahead over ESPM categories and subtypes (optional).",
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const options = useMemo(
    () => buildEspmFlatOptions(categories),
    [categories]
  );

  const selected = useMemo(
    () => findEspmFlatOption(options, value),
    [options, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return options;
    }
    return options
      .filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          (o.meta?.toLowerCase().includes(q) ?? false)
      )
      .slice(0, FILTERED_RESULT_LIMIT);
  }, [options, query]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setActiveIndex(0);
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const selectOption = (option: IEspmFlatOption) => {
    onChange(
      {
        categoryId: option.categoryId,
        detailId: option.kind === "detail" ? option.detailId : undefined,
      },
      formatEspmSelectionLabel(option)
    );
    setOpen(false);
    setQuery("");
  };

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined, "");
    setQuery("");
  };

  const onListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = filtered[activeIndex];
      if (item) {
        selectOption(item);
      }
    }
  };

  const displayLabel = formatEspmSelectionLabel(selected);

  return (
    <div className="block w-full" ref={rootRef}>
      <span className="text-xs font-semibold text-slate-700">
        {label} <span className="font-normal text-slate-500">(optional)</span>
      </span>
      <div className="relative mt-1">
        <button
          type="button"
          disabled={disabled || loading}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => !disabled && setOpen((o) => !o)}
          className={`
            flex w-full items-center justify-between gap-2 rounded-md border bg-white px-3 py-1.5 text-left text-sm
            outline-none focus:border-zeos-orange-dk focus:ring-2 focus:ring-orange-100
            disabled:cursor-not-allowed disabled:opacity-60
            ${open ? "border-zeos-orange-dk ring-2 ring-orange-100" : "border-slate-300"}
          `}
        >
          <span className={displayLabel ? "text-slate-950" : "text-slate-400"}>
            {loading
              ? "Loading property types…"
              : displayLabel || "Start typing a property type…"}
          </span>
          <span className="flex flex-none items-center gap-1">
            {displayLabel && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                title="Clear"
                className="rounded px-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                onClick={clearSelection}
              >
                ×
              </span>
            )}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              className={`text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </span>
        </button>

        {open && (
          <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-md border border-slate-300 bg-white shadow-lg">
            <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                className="flex-none text-slate-400"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.3-4.3" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onListKeyDown}
                placeholder="e.g. multifamily, office, hospital…"
                autoComplete="off"
                className="w-full border-0 bg-transparent py-0.5 text-sm outline-none"
              />
            </div>
            <ul
              role="listbox"
              className="m-0 max-h-[280px] list-none overflow-auto p-1"
            >
              {filtered.length === 0 && (
                <li className="px-3 py-3 text-center text-sm text-slate-500">
                  No matches
                </li>
              )}
              {filtered.map((option, idx) => {
                const isActive =
                  idx === activeIndex ||
                  (option.kind === "detail"
                    ? option.detailId === value?.detailId
                    : option.categoryId === value?.categoryId &&
                      value?.detailId == null);
                return (
                  <li
                    key={option.key}
                    role="option"
                    aria-selected={isActive}
                    className={`
                      flex cursor-pointer items-center justify-between gap-2 rounded px-2.5 py-2 text-sm
                      ${isActive ? "bg-slate-100" : "hover:bg-slate-50"}
                    `}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => selectOption(option)}
                  >
                    <span className="font-medium text-slate-900">
                      {option.label}
                    </span>
                    {option.kind === "detail" ? (
                      <span className="truncate text-xs text-slate-400">
                        {option.meta}
                      </span>
                    ) : (
                      <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zeos-blue">
                        Category
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
};
